<template>
  <div class="parking-cards-management">
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
          <h1 class="display-1 font-weight-bold mb-2">Tarjetas de Acceso al Estacionamiento</h1>
          <p class="subtitle grey--text">Administra las tarjetas y su vínculo con los cajones del edificio</p>
        </v-flex>
      </v-layout>

      <v-layout row wrap class="mb-4">
        <v-flex xs12 sm4>
          <v-btn color="primary" large @click="openNewCardDialog">
            <v-icon left>add</v-icon>
            Nueva Tarjeta
          </v-btn>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            v-model="searchCards"
            prepend-icon="search"
            label="Buscar tarjeta, cajón o empresa..."
            clearable
            dense
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-select
            v-model="statusFilter"
            :items="filterStatusOptions"
            item-text="text"
            item-value="value"
            label="Filtrar por estado"
            prepend-icon="filter_list"
            dense
          ></v-select>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="generalError" class="mb-3">
        <v-flex xs12>
          <v-alert type="error" dismissible :value="true" @input="generalError = ''">
            {{ generalError }}
          </v-alert>
        </v-flex>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs12>
          <v-card class="elevation-2">
            <v-data-table
              :headers="headers"
              :items="filteredCards"
              :loading="loadingCards"
              :rows-per-page-items="[10, 20, 50, 100]"
              :rows-per-page="20"
              no-data-text="No hay tarjetas registradas"
              class="elevation-1"
            >
              <template v-slot:items="props">
                <td class="text-xs-center">{{ props.item.internal_id }}</td>
                <td class="text-xs-center">{{ props.item.facility_code }}</td>
                <td class="text-xs-center">{{ props.item.card_number }}</td>
                <td class="text-xs-center">{{ props.item.department || '-' }}</td>
                <td class="text-xs-center">{{ props.item.parking_space_code || 'Sin asignar' }}</td>
                <td class="text-xs-center">{{ props.item.company_name || '-' }}</td>
                <td class="text-xs-center">
                  <v-chip :color="getStatusColor(props.item.status)" text-color="white" small>
                    {{ getStatusLabel(props.item.status) }}
                  </v-chip>
                </td>
                <td class="text-xs-center">
                  <v-icon small class="mr-3" @click="editCard(props.item)">edit</v-icon>
                  <v-icon small color="red" @click="deleteCard(props.item)">delete</v-icon>
                </td>
              </template>
              <template v-slot:no-results>
                <v-alert type="info" :value="true">No se encontraron tarjetas</v-alert>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog v-model="dialogCard" max-width="650px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ editingCard ? 'Editar Tarjeta' : 'Nueva Tarjeta' }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="formCard" v-model="validCard">
            <v-layout row wrap>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="form.internal_id"
                  label="ID interno"
                  :rules="requiredRules"
                  :error-messages="fieldErrors('internal_id')"
                  prepend-icon="badge"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="form.department"
                  label="Departamento"
                  :error-messages="fieldErrors('department')"
                  prepend-icon="business_center"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="form.facility_code"
                  label="Facility Code"
                  :rules="requiredRules"
                  :error-messages="fieldErrors('facility_code')"
                  prepend-icon="domain"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="form.card_number"
                  label="Número de tarjeta"
                  :rules="requiredRules"
                  :error-messages="fieldErrors('card_number')"
                  prepend-icon="credit_card"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-select
                  v-model="form.parking_space"
                  :items="availableSpaces"
                  item-text="displayName"
                  item-value="id"
                  label="Cajón vinculado"
                  :error-messages="fieldErrors('parking_space')"
                  prepend-icon="local_parking"
                  clearable
                ></v-select>
              </v-flex>
              <v-flex xs12 sm6>
                <v-select
                  v-model="form.status"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  label="Estado"
                  :rules="requiredRules"
                  :error-messages="fieldErrors('status')"
                  prepend-icon="info"
                ></v-select>
              </v-flex>
              <v-flex xs12>
                <v-textarea
                  v-model="form.notes"
                  label="Notas"
                  :error-messages="fieldErrors('notes')"
                  prepend-icon="notes"
                  rows="2"
                ></v-textarea>
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" flat @click="closeDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="savingCard" @click="saveCard">
            {{ editingCard ? 'Actualizar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogDelete" max-width="430px">
      <v-card>
        <v-card-title>Eliminar Tarjeta</v-card-title>
        <v-card-text>
          ¿Deseas eliminar la tarjeta
          <strong>{{ selectedCard ? selectedCard.card_number : '' }}</strong>?
          Esta acción también quitará su vínculo con el cajón.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" flat @click="dialogDelete = false">Cancelar</v-btn>
          <v-btn color="error" :loading="savingCard" @click="confirmDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import '@/plugins/axios'
import axios from 'axios'
import store from '@/store'

const API_BASE_URL = 'https://empresa-service.intrare.app'

export default {
  name: 'ParkingCardsManagement',
  data() {
    return {
      cards: [],
      spaces: [],
      loadingCards: false,
      savingCard: false,
      searchCards: '',
      statusFilter: '',
      dialogCard: false,
      dialogDelete: false,
      validCard: true,
      editingCard: null,
      selectedCard: null,
      errores: {},
      generalError: '',
      requiredRules: [value => !!value || 'Este campo es requerido'],
      form: {
        internal_id: '',
        facility_code: '',
        card_number: '',
        department: '',
        parking_space: null,
        status: 'AVAILABLE',
        notes: ''
      },
      headers: [
        { text: 'ID interno', value: 'internal_id', align: 'center' },
        { text: 'Facility Code', value: 'facility_code', align: 'center' },
        { text: 'Tarjeta', value: 'card_number', align: 'center' },
        { text: 'Departamento', value: 'department', align: 'center' },
        { text: 'Cajón', value: 'parking_space_code', align: 'center' },
        { text: 'Empresa', value: 'company_name', align: 'center' },
        { text: 'Estado', value: 'status', align: 'center' },
        { text: 'Acciones', value: 'actions', align: 'center', sortable: false }
      ],
      statusOptions: [
        { text: 'Disponible', value: 'AVAILABLE' },
        { text: 'Asignada', value: 'ASSIGNED' },
        { text: 'Deshabilitada', value: 'DISABLED' },
        { text: 'Extraviada', value: 'LOST' },
        { text: 'Dañada', value: 'DAMAGED' }
      ]
    }
  },
  computed: {
    filterStatusOptions() {
      return [{ text: 'Todos los estados', value: '' }].concat(this.statusOptions)
    },
    filteredCards() {
      const search = (this.searchCards || '').toLowerCase().trim()
      return this.cards.filter(card => {
        const matchesStatus = !this.statusFilter || card.status === this.statusFilter
        if (!matchesStatus) return false
        if (!search) return true
        return [
          card.internal_id,
          card.facility_code,
          card.card_number,
          card.department,
          card.parking_space_code,
          card.company_name
        ].some(value => value && String(value).toLowerCase().includes(search))
      })
    },
    availableSpaces() {
      const currentCardId = this.editingCard ? this.editingCard.id : null
      return this.spaces
        .filter(space => !space.access_card_id || space.access_card_id === currentCardId)
        .map(space => ({
          id: space.id,
          displayName: space.company_name
            ? `${space.code} — ${space.company_name}`
            : space.code
        }))
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    authHeaders() {
      return {
        'Content-Type': 'application/json',
        'authorization': 'Token ' + store.getters.getToken
      }
    },
    fetchData() {
      this.loadingCards = true
      this.generalError = ''
      Promise.all([
        axios.get(`${API_BASE_URL}/Parking/parking-cards/`, { headers: this.authHeaders() }),
        axios.get(`${API_BASE_URL}/Parking/building-spaces/`, { headers: this.authHeaders() })
      ])
        .then(([cardsResponse, spacesResponse]) => {
          this.cards = cardsResponse.data
          this.spaces = spacesResponse.data
        })
        .catch(error => {
          this.generalError = this.errorMessage(error, 'Error al cargar las tarjetas de estacionamiento')
        })
        .finally(() => {
          this.loadingCards = false
        })
    },
    openNewCardDialog() {
      this.editingCard = null
      this.errores = {}
      this.resetForm()
      this.dialogCard = true
    },
    editCard(card) {
      this.editingCard = card
      this.errores = {}
      this.form = {
        internal_id: card.internal_id,
        facility_code: card.facility_code,
        card_number: card.card_number,
        department: card.department || '',
        parking_space: card.parking_space || null,
        status: card.status,
        notes: card.notes || ''
      }
      this.dialogCard = true
    },
    closeDialog() {
      this.dialogCard = false
      this.editingCard = null
      this.errores = {}
      this.resetForm()
      if (this.$refs.formCard) this.$refs.formCard.resetValidation()
    },
    resetForm() {
      this.form = {
        internal_id: '',
        facility_code: '',
        card_number: '',
        department: '',
        parking_space: null,
        status: 'AVAILABLE',
        notes: ''
      }
    },
    saveCard() {
      if (!this.$refs.formCard.validate()) return
      this.errores = {}
      this.savingCard = true
      const url = this.editingCard
        ? `${API_BASE_URL}/Parking/parking-cards/${this.editingCard.id}/`
        : `${API_BASE_URL}/Parking/parking-cards/`
      axios({
        method: this.editingCard ? 'patch' : 'post',
        url,
        headers: this.authHeaders(),
        data: this.form
      })
        .then(() => {
          this.$store.commit('snackbar_change_message', 'Tarjeta guardada exitosamente')
          this.$store.commit('snackbar_activate_true')
          this.closeDialog()
          this.fetchData()
        })
        .catch(error => {
          if (error.response && error.response.status === 400 && typeof error.response.data === 'object') {
            this.errores = error.response.data
          } else {
            this.generalError = this.errorMessage(error, 'Error al guardar la tarjeta')
          }
        })
        .finally(() => {
          this.savingCard = false
        })
    },
    deleteCard(card) {
      this.selectedCard = card
      this.dialogDelete = true
    },
    confirmDelete() {
      this.savingCard = true
      axios.delete(
        `${API_BASE_URL}/Parking/parking-cards/${this.selectedCard.id}/`,
        { headers: this.authHeaders() }
      )
        .then(() => {
          this.$store.commit('snackbar_change_message', 'Tarjeta eliminada')
          this.$store.commit('snackbar_activate_true')
          this.dialogDelete = false
          this.selectedCard = null
          this.fetchData()
        })
        .catch(error => {
          this.dialogDelete = false
          this.generalError = this.errorMessage(error, 'Error al eliminar la tarjeta')
        })
        .finally(() => {
          this.savingCard = false
        })
    },
    fieldErrors(field) {
      const value = this.errores[field]
      if (!value) return []
      return Array.isArray(value) ? value : [value]
    },
    errorMessage(error, fallback) {
      if (!error.response || !error.response.data) return fallback
      return error.response.data.error || error.response.data.detail || fallback
    },
    getStatusLabel(status) {
      const option = this.statusOptions.find(item => item.value === status)
      return option ? option.text : status
    },
    getStatusColor(status) {
      const colors = {
        AVAILABLE: 'success',
        ASSIGNED: 'primary',
        DISABLED: 'grey',
        LOST: 'error',
        DAMAGED: 'warning'
      }
      return colors[status] || 'grey'
    }
  }
}
</script>

<style scoped>
.parking-cards-management {
  padding: 20px;
}
</style>
